/* ============================================================
   VEYLA | listicle G, "the switch" : the engine

   Four jobs:

   1. PAINT THE SOURCED LINES. Every brand claim, every outside price range and
      every word of the buybox comes out of vg-source.js at runtime into a
      [data-s] slot, so the claims are in one auditable file rather than
      scattered through the markup.

   2. BUILD THE FURNITURE. The ticker, the chip rail, the stat band, the trust
      list, the accordions and the image cards are all claim-bearing, so they
      are built here FROM THE REGISTER rather than typed into the page. The
      markup carries empty hosts and nothing else.

   3. PLACE THE PHOTOGRAPHS. Every image, hero and gallery included, comes from
      the ART register and is written into a [data-vg-art] slot or built here.
      One file declares the whole set with the pixel size of each file beside
      it, which is what lets the harness check a declared size against the file
      on disk: a width/height pair that does not match its file is what squashes
      a photograph, and it has done it on this brand before.

   4. ROUTE. Omar's instruction, 2026-08-13: this page no longer sells. The
      buybox that added to the real cart is gone and an offer panel stands in
      its place, and every door on the page opens the SAME product page.

   WHAT ROUTING CHANGES ABOUT THE NUMBERS. A page with a cart in it may quote
   the whole ladder, because the quote and the charge are the same surface. This
   one is not, so it quotes ONE thing, the current offer, and says on the panel
   that the price, the sizes and availability are on the product page. Every
   figure it shows is derived from a single number, so they cannot disagree with
   each other, and the panel is the only place on the page that names a price
   besides the comparison row it feeds.

   Nothing on this page is typed. Every figure is computed from the offer
   module, so a repricing moves the page, not a developer.

   No persona here, so no register kill switch: the page is Veyla in its own
   voice throughout.
   ============================================================ */
(() => {
  const O = window.VY_OFFER;
  const S = window.VG_SOURCE;
  if (!O || !S) {
    console.error('[vg] VY_OFFER or VG_SOURCE missing. Serve from the BRAND root:\n' +
      '  cd succession/brands/veyla && python3 -m http.server 8796 --bind 127.0.0.1\n' +
      '  then open /listicles/switch/');
    return;
  }

  const $  = s => document.querySelector(s);
  const $$ = s => Array.from(document.querySelectorAll(s));
  const esc = s => String(s).replace(/[&<>"]/g, m =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));

  const WEEKS_PER_TUBE = 4;

  const TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  /* Images the ENGINE applies rather than the markup. On the theme assets/ is
     flat and these relative paths do not exist, so the layout emits a map from
     local filename to CDN url and this resolves through it. */
  const imgUrl = p =>
    (window.VG_IMG && window.VG_IMG[p.split('/').pop()]) || p;

  /* Every declared shot, flat, so the gallery can name one by filename and get
     the register's own width and height with it. A thumbnail can then never
     disagree with the picture it opens. */
  const ALL_ART = [S.ART.hero, S.ART.cutout].concat(S.ART.reasons);
  const artByFile = f => ALL_ART.find(a => a.src.split('/').pop() === f);

  /* WHICH OFFER THE PAGE IS TALKING ABOUT. Nothing is selected by a reader any
     more, so this is the ladder's own default rather than a state machine: the
     bundle the product page opens on, on the plan the store defaults to. */
  const state = { tier: O.TIER_DEFAULT, sub: O.SUB_DEFAULT };

  /* The cheapest one-time entry on the ladder. ONE TIME, deliberately: the
     headline contrasts a single purchase against a single appointment, and
     quoting the subscribed price there would compare a discounted commitment
     against an undiscounted visit. */
  const entryCents = () => Math.min(...O.TIER_ORDER.map(k => O.TIERS[k].totalCents));

  /* The cheapest way ONTO the ladder however you buy, which is the same tube on
     a plan. This is what the comparison table prints (Osama, 2026-08-13), and
     the table says "on the plan" beside it: the outside ranges it sits next to
     are undiscounted, so the discount has to be named or the row is a trick.
     Computed the same way as everything else here, so a repricing moves it. */
  const planEntryComputed = () =>
    Math.min(...O.TIER_ORDER.map(k => O.tierTotal(k, true)));

  /* WHAT THE OFFER COSTS. The default bundle on a plan, which is the deal the
     panel and the table both quote. The register may override it, and today it
     does: the discount app charges a cent below what the ladder computes, and
     the app is the one that charges. See PRICE_OVERRIDE in vg-source.js.

     EVERY OTHER FIGURE ON THE PANEL COMES OFF THIS ONE, so the cent moves the
     price, the saving and the per-tube line together instead of leaving three
     numbers that disagree. */
  const dealCents = () =>
    (S.PRICE_OVERRIDE && S.PRICE_OVERRIDE.dealCents) || O.tierTotal(O.TIER_DEFAULT, O.SUB_DEFAULT);
  const planEntryCents = dealCents;
  const dealTubes = () => O.TIERS[O.TIER_DEFAULT].tubes;
  /* measured against the list price the STORE publishes, never against the
     best number elsewhere on the ladder */
  const dealSavePct = () =>
    Math.round((1 - dealCents() / (O.SERUM.rrpCents * dealTubes())) * 100);

  /* The saving on a given bundle, measured against the list price the store
     itself publishes. Never the best number elsewhere on the ladder. */
  const saveFor = (key, sub) =>
    Math.round((1 - O.tierTotal(key, sub) / O.TIERS[key].compareCents) * 100);
  /* the default bundle's own saving, which is the one the page led with */
  const headlineSave = () => saveFor(O.TIER_DEFAULT, O.SUB_DEFAULT);

  const weeks = key => O.TIERS[key].tubes * WEEKS_PER_TUBE;
  const tubes = key => {
    const n = O.TIERS[key].tubes;
    return n + ' tube' + (n === 1 ? '' : 's');
  };

  const SLOTS = {
    /* the supplied script */
    'head-top':   S.SCRIPT.headTop,
    'head-mid':   S.SCRIPT.headMid,
    'head-end':   S.SCRIPT.headEnd,
    'intro':      S.SCRIPT.intro,
    'close-head': S.SCRIPT.closeHead,
    'close':      S.SCRIPT.close,

    /* Veyla's own, always inside a labelled note */
    'brand-actives':      S.BRAND.actives,
    'brand-exosome':      S.BRAND.exosome.text,
    'brand-exosome-note': S.BRAND.exosome.note,
    'brand-minute':       S.BRAND.minute,
    'brand-stopping':     S.BRAND.stopping,
    'brand-medical':      S.BRAND.medical,
    'brand-guarantee':    S.BRAND.guarantee,

    'out-salon-cost':       S.OUTSIDE.salon.cost,
    'out-salon-mid':        S.OUTSIDE.salonMid,
    'out-transplant-cost':  S.OUTSIDE.transplant.cost,
    'out-minoxidil-cost':   S.OUTSIDE.minoxidil.cost,
    'out-finasteride-cost': S.OUTSIDE.finasteride.cost,
  };

  function paint() {
    $$('[data-s]').forEach(el => {
      const val = SLOTS[el.getAttribute('data-s')];
      if (val) el.textContent = val;
    });
    $$('[data-vg-price]').forEach(n => { n.textContent = O.money(entryCents()); });
    $$('[data-vg-price-plan]').forEach(n => { n.textContent = O.money(planEntryCents()); });
    $$('[data-save-pct]').forEach(n => { n.textContent = headlineSave() + '%'; });

    art();
    renderReasons();
    renderFurniture();
  }

  /* ---------- the photographs ----------
     One helper, used by every slot, so the width/height pair always comes from
     the register rather than from whoever wrote the markup. loading="eager" on
     the hero because it is the first thing on the page and lazy-loading the
     thing above the fold delays it for no gain. */
  const figure = (a, eager) =>
    `<img src="${imgUrl(a.src)}" alt="${esc(a.alt)}" width="${a.w}" height="${a.h}" ` +
    `loading="${eager ? 'eager' : 'lazy'}" decoding="async">`;

  function art() {
    const hero = $('[data-vg-art="hero"]');
    if (hero) hero.innerHTML = figure(S.ART.hero, true);
  }

  /* The five reasons, built from the script, each with its own photograph. The
     art alternates sides down the page, which the stylesheet does off
     :nth-child, so the order here is the script's order and nothing else.

     Numbered here rather than in the copy so the numbering cannot drift from
     the order, and so a revised script with four or six reasons still renders.
     The art is paired BY INDEX and a reason with no art still renders: a
     missing photograph should cost the page a picture, not a reason. */
  function renderReasons() {
    const host = $('[data-vg-reasons]');
    if (!host) return;
    host.innerHTML = S.SCRIPT.reasons.map((r, i) => {
      const a = S.ART.reasons[i];
      return `
      <div class="vg-reason-item">
        ${a ? `<figure class="vg-reason-shot">${figure(a)}</figure>` : ''}
        <div class="vg-reason-copy">
          <span class="vg-pill"><s></s>Reason ${i + 1}</span>
          <h2>${esc(r.head)}</h2>
          <p>${esc(r.body)}</p>
        </div>
      </div>`;
    }).join('');
  }

  /* ---------- the furniture ----------
     Ticker, chips, stat band, image cards, trust list, accordions and the
     gallery. All claim-bearing, so all built from the register. */
  function renderFurniture() {
    const band = $('[data-vg-band]');
    if (band) band.innerHTML =
      `<b>${esc(S.BRAND.band.head)}</b> <span>${esc(S.BRAND.band.note)}</span>`;

    const tick = $('[data-vg-ticker]');
    if (tick) {
      const run = S.BRAND.ticker.map(t => esc(t)).join(' <i>&bull;</i> ');
      /* twice, because the keyframe translates the track by half its width to
         loop seamlessly. One copy would slide off and leave a gap. */
      tick.innerHTML = `<span>${run} <i>&bull;</i> </span><span>${run} <i>&bull;</i> </span>`;
    }

    const chips = $('[data-vg-chips]');
    if (chips) chips.innerHTML = S.BRAND.chips
      .map(c => `<span class="${c.hi ? 'is-hi' : ''}">${esc(c.t)}</span>`).join('');

    const quad = $('[data-vg-quad]');
    if (quad) quad.innerHTML = S.BRAND.stats
      .map(s => `<div><b>${esc(s.n)}</b><span>${esc(s.l)}</span></div>`).join('');

    const cards = $('[data-vg-cards]');
    if (cards) cards.innerHTML = S.BRAND.cards.map(c => `
      <div class="vg-ic" style="background-image:url('${imgUrl(c.img)}')">
        <div class="vg-ic-tags"><i>${esc(c.tag)}</i></div>
        <h3>${esc(c.head)}</h3>
        <p>${esc(c.body)}</p>
      </div>`).join('');

    renderReviews();

    /* The two disclosures that used to live in the offer accordion. They are
       appended to the FAQ at the foot of the page rather than dropped with the
       panel they sat next to: a component can go, a disclosure cannot. */
    const faq = $('[data-vg-faq]');
    if (faq) faq.insertAdjacentHTML('beforeend', S.BRAND.faqExtra.map(a => `
      <details>
        <summary>${esc(a.q)}</summary>
        <p>${esc(a.a)}</p>
      </details>`).join(''));

  }

  /* ---------- the reviews ----------
     Real store reviews, named, painted from the register. Two slots: the pull
     quote in the article and the three-up under the FAQ.

     The star row is drawn from the review's own rating rather than fixed at
     five, because two of these are four star and drawing five over a four star
     review would be inventing a rating for a real customer. */
  const stars = n => '&#9733;'.repeat(n) + '<span style="opacity:.34">' +
    '&#9733;'.repeat(Math.max(0, 5 - n)) + '</span>';

  function renderReviews() {
    const R = S.REVIEWS;
    if (!R) return;

    const pull = $('[data-vg-pull]');
    if (pull && R.pull) pull.innerHTML =
      `<p>&ldquo;${esc(R.pull.body)}&rdquo;</p>` +
      `<cite>${esc(R.pull.name)}<span>${esc(R.pull.tag)}</span></cite>`;

    const revs = $('[data-vg-revs]');
    if (revs) revs.innerHTML = (R.list || []).map(r => `
      <div class="vg-rev">
        <div class="vg-rev-top">
          <span class="vg-rev-name">${esc(r.name)}</span>
          <span class="vg-rev-stars">${stars(r.stars)}</span>
        </div>
        <p>${esc(r.body)}</p>
      </div>`).join('');
  }

  /* ============================================================
     THE OFFER PANEL

     THIS PAGE NO LONGER SELLS (Omar, 2026-08-13). It used to carry a real
     buybox that added to the real cart; the buybox is out and the panel is a
     door to the product page instead.

     What that changes about the numbers. A selling page may quote the whole
     ladder, because the page and the cart are the same surface. A ROUTING page
     may not: the reader is about to land somewhere else, and a size, a plan or
     a saving quoted here is a promise made by a page that does not charge. So
     the panel names no price, no tier and no percentage. The one figure left on
     the page is in the comparison table, and it is the price the product page
     opens on.
     ============================================================ */

  /* The register's, not the offer module's: they are different products. See
     BUY.pdpUrl in vg-source.js. Falls back to the module's only if the register
     ever stops declaring one, because a CTA with no destination is worse than a
     CTA with an imperfect one. */
  const PDP = S.BUY.pdpUrl || O.PRODUCT_URL;

  /* Every door out of this page, in one place, so a link cannot be left
     pointing at a buybox that is not there any more. */
  function renderRoute() {
    $$('[data-vg-pdp]').forEach(a => {
      a.setAttribute('href', PDP);
      if (!a.textContent.trim()) a.textContent = S.BUY.cta;
    });

    const host = $('[data-vg-route]');
    if (!host) return;

    const price = O.money(dealCents());
    const list  = O.money(O.SERUM.rrpCents);
    const n     = dealTubes();

    host.innerHTML = `
      <div class="vg-route-shot">${figure(S.ART.cutout)}</div>

      <div class="vg-route-body">
        <span class="vg-route-badge">${esc(S.BUY.badge)}</span>
        <span class="vg-route-eyebrow">${esc(S.BUY.eyebrow)}</span>

        <!-- The only percentage on the page, and it is arithmetic: what the
             offer costs against what the store says a tube lists at. -->
        <h3 class="vg-route-head">
          <b>Save ${dealSavePct()}%</b> <em>${esc(S.BUY.headTail)}</em>
        </h3>
        <p class="vg-route-line">${n} tube${n === 1 ? '' : 's'} for ${price}. List price is ${list} a tube.</p>

        <a class="vg-btn vg-route-cta" data-vg-pdp href="${PDP}">${esc(S.BUY.cta)}</a>

        <ul class="vg-route-stats">
          ${S.BUY.stats.map(x => `<li><b>${esc(x.n)}</b><span>${esc(x.l)}</span></li>`).join('')}
        </ul>

        <p class="vg-route-note">${esc(S.BUY.note)}</p>
      </div>`;
  }

  function wire() {
    /* One delegated listener rather than one per control, because the ladder,
       the plan switch and the gallery are all rebuilt on every change and
       per-node listeners would go with them. */
    document.addEventListener('click', e => {

    });

    const sticky = $('[data-vg-sticky]');
    const hero = $('.vg-hero');
    if (sticky && hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        sticky.classList.toggle('is-in', !e.isIntersecting);
      }, { rootMargin: '-120px 0px 0px 0px' }).observe(hero);
    }

    /* the offer module re-prices itself when the currency layer lands */
    window.addEventListener('vy:currency', () => { paint(); render(); });
  }

  function render() {
    renderRoute();

    /* The bar follows the reader down the page, so it carries no price, no
       percentage and no size: a figure that travels with her is one she cannot
       check without leaving, and this page is not the one that charges. It
       names the product and its button opens the product page. */
    const st = $('[data-vg-sticky-tier]');
    const ss = $('[data-vg-sticky-sub]');
    if (st) st.textContent = S.BUY.stickyHead;
    if (ss) ss.textContent = S.BUY.stickySub;
  }

  paint();
  wire();
  render();

  window.VG = {
    state, headlineSave, saveFor, entryCents, planEntryCents, planEntryComputed, WEEKS_PER_TUBE,
    PDP, renderRoute, artByFile
  };
})();
